import { notifications } from '@mantine/notifications'
import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { usePollingQuery } from '@/hooks/usePollingQuery'

interface UsePaymentMethodRemovalProps {
  onRemovalComplete?: () => void
}

export function usePaymentMethodRemoval({ onRemovalComplete }: UsePaymentMethodRemovalProps = {}) {
  const { api } = useGlobalState()
  const { pollUntilCondition } = usePollingQuery()

  // Polling strategy to wait for payment method to be removed (become null)
  const pollForPaymentMethodRemoval = useCallback(async () => {
    return await pollUntilCondition({
      queryKey: ['billing', 'info'],
      condition: (data: any) => {
        const updatedBillingInfo = data?.billingInfo
        const updatedPaymentMethod = updatedBillingInfo?.paymentMethod

        // Payment method is successfully removed when it becomes null/undefined
        return updatedPaymentMethod === null || updatedPaymentMethod === undefined
      },
      logContext: 'Payment method removal polling',
    })
  }, [pollUntilCondition])

  // Remove payment method with polling
  const removePaymentMethodMutation = useMutation({
    mutationFn: async () => {
      return await api.removePaymentMethod({})
    },
    onSuccess: async () => {
      try {
        // Poll for payment method removal before completing
        const pollSuccess = await pollForPaymentMethodRemoval()

        // Show appropriate notification
        if (pollSuccess) {
          notifications.show({
            title: 'Success',
            message: 'Payment method removed successfully',
            color: 'green',
          })
        } else {
          notifications.show({
            title: 'Removal Processing',
            message: 'Payment method removal is complete. Changes may take a few moments to appear.',
            color: 'blue',
          })
        }

        // Call completion handler
        onRemovalComplete?.()
      } catch (error) {
        console.error('Failed to handle payment method removal completion:', error)
        notifications.show({
          title: 'Removal Error',
          message:
            'There was an issue completing the payment method removal. Please contact support if the problem persists.',
          color: 'red',
        })
      }
    },
    onError: error => {
      console.error('Failed to remove payment method:', error)
      notifications.show({
        title: 'Remove Failed',
        message: 'Unable to remove payment method. Please try again.',
        color: 'red',
      })
    },
  })

  return {
    removePaymentMethod: removePaymentMethodMutation.mutate,
    isRemoving: removePaymentMethodMutation.isPending,
    error: removePaymentMethodMutation.error,
  }
}
